import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpBackend from "i18next-http-backend";
import { getConfig } from "../config/config";

export const SUPPORTED_LANGUAGES = ["en", "ar", "es", "fr"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export interface LanguageInfo {
  code: SupportedLanguage;
  name: string;
  nativeName: string;
  direction: "ltr" | "rtl";
}

// Loaded from rms-i18n at runtime via GET /api/v1/translations/locales
let _languages: LanguageInfo[] = [];

export function getLanguages(): LanguageInfo[] {
  return _languages;
}

/** Test-only setter. Allows test-setup.ts to populate language metadata
 *  without making HTTP calls to rms-i18n. */
export function _setLanguagesForTesting(languages: LanguageInfo[]): void {
  _languages = languages;
}

export async function initializeI18n(): Promise<void> {
  if (i18n.isInitialized) return;

  const config = getConfig();
  const i18nUrl = config.i18nUrl;

  // Fetch locale metadata from rms-i18n service.
  const res = await fetch(`${i18nUrl}/api/v1/translations/locales`);
  if (!res.ok) {
    throw new Error(
      `Failed to load locales from ${i18nUrl}: ${res.status} ${res.statusText}`,
    );
  }
  _languages = (await res.json()) as LanguageInfo[];

  await i18n
    .use(HttpBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      supportedLngs: _languages.map((l) => l.code),
      defaultNS: "common",
      ns: ["common", "tasks", "chat", "obligor"],
      fallbackLng: "en",
      interpolation: {
        escapeValue: false,
      },
      backend: {
        loadPath: `${i18nUrl}/api/v1/translations/{{lng}}/{{ns}}`,
      },
      detection: {
        order: ["localStorage", "navigator"],
        caches: ["localStorage"],
        lookupLocalStorage: "priorityOSLng",
      },
      react: {
        useSuspense: true,
      },
    });
}
